import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useLogout, useMe } from "@core/auth";
import { Icon } from "@core/assets";
import { initials } from "@core/utils";

/**
 * Account card pinned to the bottom of the sidebar (Figma 325:23231 "Profile card"): a gray/50
 * filled card carrying a 40px avatar, the user's name, and their EMAIL — the design shows the
 * address here, not the role, so the role line and its bundle→label map are gone from this
 * component.
 *
 * The design draws no explicit affordance, so the whole card IS the trigger — that keeps sign-out
 * reachable without adding chrome the designer didn't draw. Built on React Aria's `MenuTrigger`,
 * so keyboard nav, focus return, and the `aria-expanded`/`aria-haspopup` wiring come for free.
 *
 * Sign-out reuses the shared `useLogout` mutation (revoke → clear client identity → route to
 * login); this component owns no auth logic of its own.
 *
 * The avatar is initials, not the photo in the Figma: the identity profile carries no avatar URL,
 * and inventing an image source would be fiction. The frame's ring + shadow are kept so the shape
 * matches; swap the inner span for an `<img>` when the contract grows an avatar field.
 */
export function AccountMenu() {
  const { t } = useTranslation("common");
  const { data: profile } = useMe();
  const logout = useLogout();

  if (!profile) return null;

  const { full_name: fullName, email } = profile.user;

  return (
    <div className="shrink-0">
      <MenuTrigger>
        <AriaButton
          aria-label={t("layout.account-menu")}
          className={
            "flex w-full items-center gap-sm rounded-sm bg-gray-50 p-sm text-left " +
            "outline-none transition-colors " +
            "hover:bg-gray-100 pressed:bg-gray-200 " +
            "focus-visible:ring-2 focus-visible:ring-focus-ring"
          }
        >
          <span
            aria-hidden="true"
            className={
              "flex size-10 shrink-0 items-center justify-center rounded-full " +
              "border-4 border-white bg-gray-100 shadow-lg " +
              "text-xs-semibold text-(--text-secondary)"
            }
          >
            {initials(fullName)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm-semibold text-(--text-primary)">
              {fullName}
            </span>
            <span className="truncate text-xs-regular text-(--text-secondary)">
              {email}
            </span>
          </span>
        </AriaButton>

        <Popover
          placement="top start"
          className={
            "min-w-56 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border " +
            "border-(--border-default) bg-surface-card shadow-lg outline-none"
          }
        >
          <Menu
            aria-label={t("layout.account-menu")}
            className="p-xxs outline-none"
            disabledKeys={logout.isPending ? ["sign-out"] : []}
            onAction={(key) => {
              if (key === "sign-out") logout.mutate();
            }}
          >
            <MenuItem
              id="sign-out"
              className={
                "flex cursor-pointer items-center gap-sm rounded-md px-sm py-2.5 outline-none " +
                "text-sm-medium text-(--text-secondary) " +
                "hover:bg-gray-50 focus:bg-gray-50 focus:text-(--text-primary) " +
                "disabled:cursor-not-allowed disabled:text-(--text-disabled)"
              }
            >
              <Icon
                name={logout.isPending ? "loading" : "log-out"}
                size={18}
                className={logout.isPending ? "animate-spin" : undefined}
              />
              {t("layout.sign-out")}
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
}
